import { useState, useRef, useEffect, useMemo } from 'react'
import {
  Car,
  Truck,
  Home,
  Bike,
  Ship,
  Plane,
  Train,
  Bus,
  Caravan,
  Tractor,
  Forklift,
  Container,
  Wrench,
  Hammer,
  PaintBucket,
  Drill,
  HardHat,
  Construction,
  Shield,
  ShieldCheck,
  ShieldPlus,
  Lock,
  Key,
  Laptop,
  Monitor,
  Smartphone,
  Tablet,
  Tv,
  Printer,
  Camera,
  Headphones,
  Speaker,
  Watch,
  Cpu,
  Server,
  Wifi,
  Refrigerator,
  WashingMachine,
  CookingPot,
  Microwave,
  Fan,
  AirVent,
  Lightbulb,
  Plug,
  Sofa,
  Bed,
  Armchair,
  Lamp,
  DoorOpen,
  Fence,
  Trees,
  Flower2,
  Droplets,
  Thermometer,
  Zap,
  Battery,
  Sun,
  CloudSun,
  Umbrella,
  Package,
  Box,
  ShoppingBag,
  ShoppingCart,
  Tag,
  Gift,
  Heart,
  Star,
  Award,
  Crown,
  Gem,
  Puzzle,
  Blocks,
  Gamepad2,
  Dumbbell,
  Stethoscope,
  Pill,
  Baby,
  Dog,
  Cat,
  Bird,
  Fish,
  Wheat,
  Apple,
  Grape,
  Wine,
  Coffee,
  UtensilsCrossed,
  Scissors,
  Paintbrush,
  Music,
  BookOpen,
  GraduationCap,
  Globe,
  Map,
  Compass,
  Mountain,
  Tent,
  Flame,
  Anchor,
  Rocket,
  Building,
  Building2,
  Factory,
  Warehouse,
  Church,
  LandPlot,
  CircuitBoard,
  Cog,
  type LucideIcon,
} from 'lucide-react'

import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { X } from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'

// ---------------------------------------------------------------------------
// Icon registry - curated set of Lucide icons for the picker
// ---------------------------------------------------------------------------

interface IconEntry {
  key: string
  label: string
  Icon: LucideIcon
  tags: string // space-separated search keywords
}

const ICON_REGISTRY: IconEntry[] = [
  // Vehicles
  { key: 'car', label: 'Car', Icon: Car, tags: 'vehicle auto automobile sedan' },
  { key: 'truck', label: 'Truck', Icon: Truck, tags: 'vehicle lorry delivery van' },
  { key: 'bike', label: 'Motorcycle', Icon: Bike, tags: 'motorcycle motorbike cycle bicycle' },
  { key: 'ship', label: 'Ship', Icon: Ship, tags: 'boat vessel marine yacht' },
  { key: 'plane', label: 'Plane', Icon: Plane, tags: 'aircraft airplane flight aviation' },
  { key: 'train', label: 'Train', Icon: Train, tags: 'railway rail transit' },
  { key: 'bus', label: 'Bus', Icon: Bus, tags: 'transit transport coach' },
  { key: 'caravan', label: 'Caravan', Icon: Caravan, tags: 'motorhome rv camper trailer' },
  { key: 'tractor', label: 'Tractor', Icon: Tractor, tags: 'farm agriculture machinery' },
  { key: 'forklift', label: 'Forklift', Icon: Forklift, tags: 'warehouse industrial lift' },
  { key: 'container', label: 'Container', Icon: Container, tags: 'shipping cargo freight' },

  // Buildings & Property
  { key: 'home', label: 'Home', Icon: Home, tags: 'house property residential dwelling' },
  { key: 'building', label: 'Building', Icon: Building, tags: 'office commercial tower' },
  { key: 'building2', label: 'Office', Icon: Building2, tags: 'corporate headquarters business' },
  { key: 'factory', label: 'Factory', Icon: Factory, tags: 'industrial manufacturing plant' },
  { key: 'warehouse', label: 'Warehouse', Icon: Warehouse, tags: 'storage depot' },
  { key: 'church', label: 'Church', Icon: Church, tags: 'religious temple' },
  { key: 'land-plot', label: 'Land', Icon: LandPlot, tags: 'property lot plot real estate' },
  { key: 'door-open', label: 'Door', Icon: DoorOpen, tags: 'entrance entry' },
  { key: 'fence', label: 'Fence', Icon: Fence, tags: 'boundary garden outdoor' },

  // Tools & Construction
  { key: 'wrench', label: 'Wrench', Icon: Wrench, tags: 'tool repair fix spanner' },
  { key: 'hammer', label: 'Hammer', Icon: Hammer, tags: 'tool build construction' },
  { key: 'paint-bucket', label: 'Paint', Icon: PaintBucket, tags: 'paint color decor' },
  { key: 'drill', label: 'Drill', Icon: Drill, tags: 'tool power' },
  { key: 'hard-hat', label: 'Hard Hat', Icon: HardHat, tags: 'safety helmet construction' },
  { key: 'construction', label: 'Construction', Icon: Construction, tags: 'build roadworks barrier' },

  // Security & Protection
  { key: 'shield', label: 'Shield', Icon: Shield, tags: 'protection security guard safety' },
  { key: 'shield-check', label: 'Verified', Icon: ShieldCheck, tags: 'protection warranty verified' },
  { key: 'shield-plus', label: 'Coverage', Icon: ShieldPlus, tags: 'protection warranty extended' },
  { key: 'lock', label: 'Lock', Icon: Lock, tags: 'security padlock' },
  { key: 'key', label: 'Key', Icon: Key, tags: 'access unlock' },

  // Electronics & Tech
  { key: 'laptop', label: 'Laptop', Icon: Laptop, tags: 'computer notebook pc' },
  { key: 'monitor', label: 'Monitor', Icon: Monitor, tags: 'screen display desktop' },
  { key: 'smartphone', label: 'Phone', Icon: Smartphone, tags: 'mobile cell device' },
  { key: 'tablet', label: 'Tablet', Icon: Tablet, tags: 'ipad device' },
  { key: 'tv', label: 'TV', Icon: Tv, tags: 'television screen display' },
  { key: 'printer', label: 'Printer', Icon: Printer, tags: 'print scanner' },
  { key: 'camera', label: 'Camera', Icon: Camera, tags: 'photo photography lens' },
  { key: 'headphones', label: 'Headphones', Icon: Headphones, tags: 'audio music earphones' },
  { key: 'speaker', label: 'Speaker', Icon: Speaker, tags: 'audio sound music' },
  { key: 'watch', label: 'Watch', Icon: Watch, tags: 'time clock wearable' },
  { key: 'cpu', label: 'CPU', Icon: Cpu, tags: 'processor chip computer' },
  { key: 'server', label: 'Server', Icon: Server, tags: 'hosting rack datacenter' },
  { key: 'wifi', label: 'Wi-Fi', Icon: Wifi, tags: 'wireless internet network' },
  { key: 'circuit-board', label: 'Circuit', Icon: CircuitBoard, tags: 'electronics pcb board' },

  // Appliances & Home
  { key: 'refrigerator', label: 'Fridge', Icon: Refrigerator, tags: 'appliance kitchen cold' },
  { key: 'washing-machine', label: 'Washer', Icon: WashingMachine, tags: 'appliance laundry' },
  { key: 'cooking-pot', label: 'Cookware', Icon: CookingPot, tags: 'kitchen cooking pot pan' },
  { key: 'microwave', label: 'Microwave', Icon: Microwave, tags: 'appliance kitchen oven' },
  { key: 'fan', label: 'Fan', Icon: Fan, tags: 'cooling air appliance' },
  { key: 'air-vent', label: 'HVAC', Icon: AirVent, tags: 'heating cooling ventilation ac' },
  { key: 'lightbulb', label: 'Light', Icon: Lightbulb, tags: 'lamp lighting bulb led' },
  { key: 'plug', label: 'Plug', Icon: Plug, tags: 'electrical outlet socket power' },

  // Furniture
  { key: 'sofa', label: 'Sofa', Icon: Sofa, tags: 'furniture couch seating living room' },
  { key: 'bed', label: 'Bed', Icon: Bed, tags: 'furniture bedroom mattress' },
  { key: 'armchair', label: 'Chair', Icon: Armchair, tags: 'furniture seating' },
  { key: 'lamp', label: 'Lamp', Icon: Lamp, tags: 'furniture lighting desk' },

  // Energy & Utilities
  { key: 'zap', label: 'Energy', Icon: Zap, tags: 'electric power lightning bolt' },
  { key: 'battery', label: 'Battery', Icon: Battery, tags: 'power charge energy' },
  { key: 'sun', label: 'Solar', Icon: Sun, tags: 'solar energy panel renewable' },
  { key: 'flame', label: 'Heating', Icon: Flame, tags: 'fire gas boiler' },
  { key: 'droplets', label: 'Water', Icon: Droplets, tags: 'plumbing liquid rain' },
  { key: 'thermometer', label: 'Climate', Icon: Thermometer, tags: 'temperature heat cold' },
  { key: 'cloud-sun', label: 'Weather', Icon: CloudSun, tags: 'outdoor climate' },
  { key: 'umbrella', label: 'Umbrella', Icon: Umbrella, tags: 'protection rain cover' },

  // Outdoor & Nature
  { key: 'trees', label: 'Trees', Icon: Trees, tags: 'garden landscape nature forest' },
  { key: 'flower', label: 'Flower', Icon: Flower2, tags: 'garden plant floral' },
  { key: 'mountain', label: 'Mountain', Icon: Mountain, tags: 'outdoor terrain hill' },
  { key: 'tent', label: 'Tent', Icon: Tent, tags: 'camping outdoor recreation' },
  { key: 'anchor', label: 'Anchor', Icon: Anchor, tags: 'marine nautical boat' },
  { key: 'compass', label: 'Compass', Icon: Compass, tags: 'navigation direction travel' },

  // Retail & Commerce
  { key: 'package', label: 'Package', Icon: Package, tags: 'box parcel delivery shipping' },
  { key: 'box', label: 'Box', Icon: Box, tags: 'container storage crate' },
  { key: 'shopping-bag', label: 'Bag', Icon: ShoppingBag, tags: 'retail shop store' },
  { key: 'shopping-cart', label: 'Cart', Icon: ShoppingCart, tags: 'retail shop store purchase' },
  { key: 'tag', label: 'Tag', Icon: Tag, tags: 'label price category' },
  { key: 'gift', label: 'Gift', Icon: Gift, tags: 'present reward bonus' },

  // Values & Awards
  { key: 'heart', label: 'Heart', Icon: Heart, tags: 'love care health favorite' },
  { key: 'star', label: 'Star', Icon: Star, tags: 'favorite rating premium' },
  { key: 'award', label: 'Award', Icon: Award, tags: 'medal trophy achievement' },
  { key: 'crown', label: 'Crown', Icon: Crown, tags: 'premium luxury royal' },
  { key: 'gem', label: 'Gem', Icon: Gem, tags: 'jewelry precious diamond luxury' },
  { key: 'rocket', label: 'Rocket', Icon: Rocket, tags: 'launch startup speed' },

  // Leisure & Recreation
  { key: 'puzzle', label: 'Puzzle', Icon: Puzzle, tags: 'game toy play' },
  { key: 'blocks', label: 'Blocks', Icon: Blocks, tags: 'toy building lego kids' },
  { key: 'gamepad', label: 'Gaming', Icon: Gamepad2, tags: 'game console controller play' },
  { key: 'dumbbell', label: 'Fitness', Icon: Dumbbell, tags: 'gym sports exercise' },
  { key: 'music', label: 'Music', Icon: Music, tags: 'audio sound instrument' },

  // Health & Living
  { key: 'stethoscope', label: 'Medical', Icon: Stethoscope, tags: 'health doctor hospital' },
  { key: 'pill', label: 'Pill', Icon: Pill, tags: 'medicine pharmacy health' },
  { key: 'baby', label: 'Baby', Icon: Baby, tags: 'child infant kids' },

  // Animals
  { key: 'dog', label: 'Dog', Icon: Dog, tags: 'pet animal canine' },
  { key: 'cat', label: 'Cat', Icon: Cat, tags: 'pet animal feline' },
  { key: 'bird', label: 'Bird', Icon: Bird, tags: 'animal pet avian' },
  { key: 'fish', label: 'Fish', Icon: Fish, tags: 'animal aquatic marine pet' },

  // Food & Drink
  { key: 'wheat', label: 'Grain', Icon: Wheat, tags: 'food agriculture crop cereal' },
  { key: 'apple', label: 'Apple', Icon: Apple, tags: 'food fruit produce' },
  { key: 'grape', label: 'Grape', Icon: Grape, tags: 'food fruit wine' },
  { key: 'wine', label: 'Wine', Icon: Wine, tags: 'drink beverage alcohol' },
  { key: 'coffee', label: 'Coffee', Icon: Coffee, tags: 'drink beverage cafe' },
  { key: 'utensils', label: 'Dining', Icon: UtensilsCrossed, tags: 'food restaurant kitchen cutlery' },

  // Misc
  { key: 'scissors', label: 'Scissors', Icon: Scissors, tags: 'cut craft tool' },
  { key: 'paintbrush', label: 'Art', Icon: Paintbrush, tags: 'paint art creative design' },
  { key: 'book-open', label: 'Book', Icon: BookOpen, tags: 'reading education' },
  { key: 'graduation-cap', label: 'Education', Icon: GraduationCap, tags: 'school university learning' },
  { key: 'globe', label: 'Globe', Icon: Globe, tags: 'world international earth web' },
  { key: 'map', label: 'Map', Icon: Map, tags: 'location geography navigation' },
  { key: 'cog', label: 'Settings', Icon: Cog, tags: 'gear settings config general' },
]

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  ICON_REGISTRY.map((e) => [e.key, e.Icon]),
)

// eslint-disable-next-line react-refresh/only-export-components
export const ICON_OPTIONS: { key: string; label: string }[] = ICON_REGISTRY.map(
  ({ key, label }) => ({ key, label }),
)

export const DEFAULT_ICON_KEY = 'package'

export function CategoryIcon({ icon, className }: { icon: string; className?: string }) {
  const Icon = ICON_MAP[icon]
  if (Icon) return <Icon size={24} className={className} />
  // Unknown names (and the tick before the lazy icon set lands) fall back to
  // the default; LucideIconByName resolves validity itself.
  const Fallback = ICON_MAP[DEFAULT_ICON_KEY]
  return (
    <LucideIconByName
      name={icon}
      size={24}
      className={className}
      fallback={<Fallback size={24} className={className} />}
    />
  )
}

// ---------------------------------------------------------------------------
// IconPicker - reusable picker with embed & floating modes
// ---------------------------------------------------------------------------

function IconGrid({
  search,
  value,
  onSelect,
}: {
  search: string
  value: string
  onSelect: (key: string) => void
}) {
  const filtered = useMemo(() => {
    if (!search) return ICON_REGISTRY
    const q = search.toLowerCase()
    return ICON_REGISTRY.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.key.includes(q) ||
        e.tags.includes(q),
    )
  }, [search])

  if (filtered.length === 0) {
    return (
      <p className="py-6 text-center text-[11px] text-[var(--ink-3)]">
        No icons match &ldquo;{search}&rdquo;
      </p>
    )
  }

  return (
    <div className="grid grid-cols-6 gap-1">
      {filtered.map((entry) => {
        const selected = value === entry.key
        return (
          <Button
            key={entry.key}
            variant="ghost"
            size="sm"
            title={entry.label}
            onClick={() => onSelect(selected ? '' : entry.key)}
            className={cn(
              'flex h-auto cursor-pointer flex-col items-center gap-1 rounded-[6px] border-[0.5px] px-1 py-2 transition-all duration-150',
              selected
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                : 'border-transparent text-[var(--ink-3)] hover:border-[var(--rule)] hover:bg-[var(--bg-light)]',
            )}
          >
            <entry.Icon
              size={18}
              strokeWidth={1.5}
              className={cn(selected ? 'text-[var(--green)]' : 'text-[var(--ink-3)]')}
            />
            <span className="max-w-full truncate text-[9px] leading-none">{entry.label}</span>
          </Button>
        )
      })}
    </div>
  )
}

export function IconPicker({
  value,
  onChange,
  mode = 'floating',
  label,
}: {
  value: string
  onChange: (key: string) => void
  mode?: 'embed' | 'floating'
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Close floating picker on outside click
  useEffect(() => {
    if (mode !== 'floating' || !open) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [mode, open])

  const selectedEntry = ICON_REGISTRY.find((e) => e.key === value)

  const pickerContent = (
    <div className="flex flex-col gap-2">
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search icons…"
        className="h-8 text-[12px]"
      />
      <div className="max-h-[240px] overflow-y-auto overscroll-contain">
        <IconGrid search={search} value={value} onSelect={(key) => {
          onChange(key)
          if (mode === 'floating') setOpen(false)
        }} />
      </div>
    </div>
  )

  // ── Embed mode: inline panel ──
  if (mode === 'embed') {
    return (
      <div className="flex flex-col gap-1.5">
        {label && <Label className="clara-section-label mb-0 block">{label}</Label>}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2.5">
          {pickerContent}
        </div>
      </div>
    )
  }

  // ── Floating mode: trigger + popover ──
  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      {label && <Label className="clara-section-label mb-0 block">{label}</Label>}
      <Button
        variant="ghost"
        onClick={() => { setOpen(!open); setSearch('') }}
        className={cn(
          'flex h-9 w-full cursor-pointer items-center justify-start gap-2.5 rounded-[4px] border-[0.5px] bg-[var(--bg)] px-3 text-left text-[13px] font-normal transition-colors duration-150',
          open ? 'border-[var(--green)]' : 'border-[var(--rule)]',
        )}
      >
        {selectedEntry ? (
          <>
            <selectedEntry.Icon size={16} strokeWidth={1.5} className="shrink-0 text-[var(--ink-2)]" />
            <span className="text-[var(--ink)]">{selectedEntry.label}</span>
          </>
        ) : (
          <span className="text-[var(--ink-3)]">Choose an icon…</span>
        )}
        {value && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="ml-auto h-5 w-5 text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <X size={14} />
          </Button>
        )}
      </Button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-full rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2.5 shadow-[0_4px_12px_var(--overlay-light)]">
          {pickerContent}
        </div>
      )}
    </div>
  )
}
